import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from "@angular/core";
import { Router, UrlTree } from "@angular/router";
import { EvidenceRequirementsLaunchService } from "../../services/evidence_requirements_launch.service";
import { ProductLicensesService } from "../../services/product_licenses.service";
import { encodeBase64RouteValue, encodeRouteId } from "../../utils/base64_route_value.util";
import { NzSelectComponent } from "ng-zorro-antd/select";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { Subject, Subscription, forkJoin, map, finalize } from "rxjs";
import { AccountSettingsService } from "../../services/account_settings.service";
import { AddUserLaunchService } from "../../services/add_user_launch.service";
import { assessmentTaskSessionIdentity } from "../../services/assessment_task_session.util";
import { UserAccessError } from "../../services/assessment_user_access.util";
import { invitationResultMessage } from "../add_user_modal/invitation_result.util";
import { UserDialogOrigin } from "../../services/user_dialog_origin.util";
import { AssessmentWorkItemsService } from "../../services/assessment_work_items.service";
import { AssessmentWorkItemEvidenceDetail } from "../../services/assessment_work_items.types";
import { AssessmentTaskInvalidationService } from "../../services/assessment_task_invalidation.service";
import { EvidenceRequirementInvalidationService } from "../../services/evidence_requirement_invalidation.service";
import { LoginService } from "../../services/login.service";
import {
    calendarDateToPickerDate, isAllowedDueDate, isPickerDateDisabled, pickerDateToCalendarDate
} from "../../utils/assessment_task_date.util";
import { evidenceRequirementDueState } from "../../utils/evidence_requirement_due_date.util";
import {
    EvidenceRequirementsService,
    BulkUpdateEvidenceRequirementsPayload,
    CreateEvidenceRequirementPayload,
    UpdateEvidenceRequirementPayload,
    EvidenceRecipientSummary,
    EvidenceRequirementStatus,
    EvidenceSecureStorageFileOption,
    EvidenceUploadUserOption,
    PersistedEvidenceRequirement
} from "../../services/evidence_requirements.service";

import {
    buildPersistedEvidenceItemCopyText,
    buildPersistedEvidenceItemCsv,
    PersistedEvidenceItemExportRow
} from "./evidence_requirements_modal.util";

export interface EvidenceModalEmailRequest {
    productLicenseId: number;
    evidenceRequirementIds: number[];
}

export interface EvidenceModalLinkedFileRequest {
    evidenceRequirementId: number;
    secureStorageFileId: number;
    action: "download" | "preview" | "manage";
    openInNewTab?: boolean;
}

export interface EvidenceModalUploadRequest {
    productLicenseId: number;
    evidenceRequirementId: number;
}

interface EvidenceAssignedUserFilterOption {
    userId: number;
    displayName: string;
}

type EvidenceRequirementItemFormMode = "create" | "edit";
type BulkRequirementSelectionMode = "unchanged" | "all" | "specific";
type BulkAssignmentSelectionMode = "unchanged" | "unassigned" | "specific";
type BulkDueDateMode = "unchanged" | "set" | "clear";
type EvidenceItemSort = "created_at_desc" | "created_at_asc" | "updated_at_desc" | "updated_at_asc";

interface EvidenceRequirementItemFormState {
    evidenceRequirementId: number | null;
    evidenceName: string;
    status: EvidenceRequirementStatus;
    dueDate: string | null;
    requirementKeys: string[];
    assignedUserIds: number[];
    linkedSecureStorageFileId: number | null;
    allRequirements: boolean;
}

interface BulkEvidenceItemFormState {
    evidenceName: string;
    status: EvidenceRequirementStatus | null;
    dueDateMode: BulkDueDateMode;
    dueDate: string | null;
    requirementSelectionMode: BulkRequirementSelectionMode;
    requirementKeys: string[];
    assignmentSelectionMode: BulkAssignmentSelectionMode;
    assignedUserIds: number[];
}

@Component({
    selector: "app-evidence-requirements-modal",
    templateUrl: "./evidence_requirements_modal.component.html",
    styleUrls: ["./evidence_requirements_modal.component.scss"]
})
export class EvidenceRequirementsModalComponent implements OnChanges, OnDestroy {
    @Input() productLicenseId: number | null = null;
    @Input() exactEvidenceId: number | null = null;
    @Input() initialItemAction: "edit" | "delete" | "email" | null = null;
    private initialItemActionHandled = false;
    private closingItemModals = false;
    get isDirectItemAction(): boolean { return this.isExactItemMode && this.initialItemAction !== null; }
    @Input() expectedMerchantId: number | null = null;
    @Input() visible = false;
    @Input() initialStatusFilter: EvidenceRequirementStatus | null = null;
    @Input() loading = false;
    @Input() errorMessage = "";
    @Input() canOpenEvidenceUpload = false;
    persistedEvidenceItems: PersistedEvidenceRequirement[] = [];
    displayedEvidenceItems: PersistedEvidenceRequirement[] = [];
    uploaderOptions: EvidenceUploadUserOption[] = [];
    secureStorageFileOptions: EvidenceSecureStorageFileOption[] = [];
    requirementOptions: string[] = [];
    canManagePersistedEvidenceItems = false;
    exactAssessmentRoute: UrlTree | null = null;
    exactEvidenceDetail: AssessmentWorkItemEvidenceDetail | null = null;

    recipientSummary: EvidenceRecipientSummary | null = null;
    recipientLoading = false;
    recipientErrorMessage = "";
    @Input() emailSending = false;
    @Input() emailSuccessMessage = "";
    @Input() emailFailureMessage = "";

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() emailRequested = new EventEmitter<EvidenceModalEmailRequest>();
    @Output() emailStatusCleared = new EventEmitter<void>();
    @Output() persistedEvidenceItemsChanged = new EventEmitter<readonly PersistedEvidenceRequirement[]>();
    // The caller owns Secure Storage navigation; the modal emits only selected IDs.
    @Output() linkedFileRequested = new EventEmitter<EvidenceModalLinkedFileRequest>();
    @Output() uploadRequested = new EventEmitter<EvidenceModalUploadRequest>();

    copyErrorMessage = "";
    emailErrorMessage = "";
    isEmailPanelVisible = false;
    private emailItemId: number | null = null;
    evidenceSearchQuery = "";
    evidenceFiltersVisible = false;
    selectedEvidenceSort: EvidenceItemSort = "created_at_desc";
    readonly evidenceSortOptions: {
        value: EvidenceItemSort; label: string; icon: string; accessibleLabel: string;
        field: "createdAt" | "updatedAt"; direction: number;
    }[] = [
        { value: "created_at_desc", label: "Created At", icon: "arrow-down", accessibleLabel: "Created At descending", field: "createdAt", direction: -1 },
        { value: "created_at_asc", label: "Created At", icon: "arrow-up", accessibleLabel: "Created At ascending", field: "createdAt", direction: 1 },
        { value: "updated_at_desc", label: "Updated At", icon: "arrow-down", accessibleLabel: "Updated At descending", field: "updatedAt", direction: -1 },
        { value: "updated_at_asc", label: "Updated At", icon: "arrow-up", accessibleLabel: "Updated At ascending", field: "updatedAt", direction: 1 }
    ];
    selectedEvidenceItemIds = new Set<number>();
    selectedStatusFilters: EvidenceRequirementStatus[] = [];
    selectedAssignedUserFilters: number[] = [];
    assignedUserFilterOptions: EvidenceAssignedUserFilterOption[] = [];
    itemFormModalVisible = false;
    itemUserInviteAllowed = false;
    itemUserChildOpen = false;
    itemUserReturnPending = false;
    itemUserRecheckFailed = false;
    itemAssigneeDropdownOpen = false;
    @ViewChild("evidenceItemOrigin") private evidenceItemOrigin?: ElementRef<HTMLElement>;
    @ViewChild("evidenceItemAssignees") private evidenceItemAssignees?: NzSelectComponent;
    @ViewChild("bulkEvidenceOrigin") private bulkEvidenceOrigin?: ElementRef<HTMLElement>;
    @ViewChild("bulkEvidenceAssignees") private bulkEvidenceAssignees?: NzSelectComponent;
    private readonly itemUserOriginChanges = new Subject<void>();
    private itemUserRevision = 0;
    private itemUserCapabilityRequest?: Subscription;
    private itemUserChildRequest?: Subscription;
    private itemUserReturnRequest?: Subscription;
    private itemUserScopeRefreshPending = false;
    private uploaderOptionVersion = 0;
    deleteConfirmationModalVisible = false;
    itemFormMode: EvidenceRequirementItemFormMode = "create";
    itemFormState: EvidenceRequirementItemFormState = this.createEmptyItemFormState();
    itemDueDatePickerValue: Date | null = null;
    itemDueDateTouched = false;
    itemDueDateInvalid = false;
    private itemFormInitialState: EvidenceRequirementItemFormState | null = null;
    private itemFormInitializing = false;
    private itemFormInitializationId = 0;
    itemPendingDeletion: Pick<PersistedEvidenceRequirement, "id" | "evidenceName"> | null = null;
    bulkEditModalVisible = false;
    bulkEditItemIds: number[] = [];
    bulkEditFormState: BulkEvidenceItemFormState = this.createEmptyBulkEditFormState();
    bulkDueDatePickerValue: Date | null = null;
    bulkDueDateTouched = false;
    bulkDueDateInvalid = false;
    bulkEditErrorMessage = "";
    itemFormErrorMessage = "";
    deleteConfirmationErrorMessage = "";
    isItemMutationInProgress = false;
    private emailSubmissionPending = false;
    private serverRequestId = 0;
    private recipientRequestId = 0;
    private recipientSummaryEvidenceIds: number[] = [];
    private itemMutationRequestId = 0;
    private copyRequestId = 0;
    private assignedUserDisplayNamesById = new Map<number, string>();
    private listRequestSession: { accountId: number; userId: number; token: string } | null = null;
    private exactRequestSession: { accountId: number; userId: number; token: string } | null = null;
    private exactRefreshPending = false;
    exactDraftStale = false;
    private listRefreshPending = false;
    private listRefreshTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly userUpdatesSubscription: Subscription;
    private readonly evidenceChangesSubscription: Subscription;
    private readonly scopeChangesSubscription: Subscription;

    readonly statusLabels: Record<PersistedEvidenceRequirement["status"], string> = {
        not_submitted: "Not Submitted",
        file_pending_review: "File Pending Review",
        file_rejected: "File Rejected",
        submitted_reviewed: "Submitted & Reviewed"
    };

    readonly evidenceRequirementStatuses: EvidenceRequirementStatus[] = [
        "not_submitted",
        "file_pending_review",
        "file_rejected",
        "submitted_reviewed"
    ];
    readonly evidenceDueState = evidenceRequirementDueState;

    constructor(
        private readonly evidenceRequirementsService: EvidenceRequirementsService,
        private readonly assessmentWorkItemsService: AssessmentWorkItemsService,
        private readonly evidenceInvalidations: EvidenceRequirementInvalidationService,
        private readonly taskInvalidations: AssessmentTaskInvalidationService,
        private readonly loginService: LoginService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly notification: NzNotificationService,
        private readonly accountSettings: AccountSettingsService,
        private readonly addUserLauncher: AddUserLaunchService,
        private readonly productLicenses: ProductLicensesService,
        private readonly router: Router,
        private readonly evidenceLauncher: EvidenceRequirementsLaunchService
    ) {
        this.userUpdatesSubscription = this.loginService.getUserUpdates().subscribe(() => {
            if (!this.visible) return;
            const session = this.isExactItemMode ? this.exactRequestSession : this.listRequestSession;
            if (!session ||
                (session.accountId === this.loginService.getCurrentAccountId() &&
                    session.userId === this.loginService.getCurrentUserId() &&
                    session.token === this.loginService.getToken())) return;

            this.cancelItemUserFlow();

            if (this.isExactItemMode) {
                this.showExactItemUnavailable();
            } else {
                // Clear old-account rows and options before requesting the new session's scope.
                this.clearPersistedItemContext("This assessment is unavailable in your current scope.");
                this.clearEvidenceItemFilters();
                this.evidenceSearchQuery = "";
                this.selectedEvidenceSort = "created_at_desc";
                if (this.loginService.getCurrentAccountId() && this.loginService.getCurrentUserId() &&
                    this.loginService.getToken()) this.loadServerData(false);
            }
            this.changeDetectorRef.markForCheck();
        });
        this.evidenceChangesSubscription = this.evidenceInvalidations.watchChanges().subscribe((change) => {
            if (!this.visible || change.accountId !== this.loginService.getCurrentAccountId() ||
                change.productLicenseId !== this.productLicenseId) return;
            // The shared comments controller reloads its exact authorized parent independently.
            // Replacing the modal for a comment would discard open panels and edit drafts.
            if (change.kind === "comment") return;
            if (!this.isExactItemMode) {
                this.requestListRefresh();
                return;
            }
            const session = this.exactRequestSession;
            if (!session || session.accountId !== change.accountId ||
                session.userId !== this.loginService.getCurrentUserId() ||
                session.token !== this.loginService.getToken() ||
                (change.items !== null && !change.items.some((item) => item.id === this.exactEvidenceId))) return;
            if (!this.isItemMutationInProgress &&
                (this.itemFormModalVisible || this.deleteConfirmationModalVisible)) {
                this.markExactDraftStale();
                this.loadExactEvidenceDetail();
                return;
            }
            this.exactRefreshPending = true;
            this.refreshExactItemWhenReady();
        });
        this.scopeChangesSubscription = this.taskInvalidations.watchInvalidations().subscribe((event) => {
            if (event.kind !== "scope" || !this.visible || !this.productLicenseId ||
                (event.productLicenseId !== null && event.productLicenseId !== this.productLicenseId)) return;
            // Recheck access even when no evidence row changed.
            if (this.itemUserChildOpen || this.itemUserReturnPending || this.isItemMutationInProgress) {
                this.itemUserScopeRefreshPending = true;
                if (this.isExactItemMode) this.exactRefreshPending = true;
                else this.listRefreshPending = true;
                return;
            }
            if (this.itemFormModalVisible && !this.isItemMutationInProgress) {
                this.refreshItemUserEligibility();
                this.refreshItemInvitationCapability();
                return;
            } else if (this.bulkEditModalVisible && !this.isItemMutationInProgress) {
                this.refreshBulkUserEligibility();
                this.refreshItemInvitationCapability();
                return;
            }
            if (this.isExactItemMode) {
                if (this.deleteConfirmationModalVisible) this.markExactDraftStale();
                this.loadExactEvidenceDetail();
            }
            else {
                this.canManagePersistedEvidenceItems = false;
                this.loadServerData(false);
            }
        });
    }

    @HostListener("window:focus")
    onWindowFocus(): void {
        this.refreshExactItemWhenReady();
    }

    ngOnDestroy(): void {
        this.cancelItemUserFlow();
        this.itemUserOriginChanges.complete();
        this.itemFormInitializationId += 1;
        this.serverRequestId += 1;
        this.recipientRequestId += 1;
        this.itemMutationRequestId += 1;
        this.listRequestSession = null;
        this.exactRequestSession = null;
        this.exactRefreshPending = false;
        this.clearListRefresh();
        this.userUpdatesSubscription.unsubscribe();
        this.evidenceChangesSubscription.unsubscribe();
        this.scopeChangesSubscription.unsubscribe();
    }

    private refreshExactItemWhenReady(): void {
        const session = this.exactRequestSession;
        if (!this.exactRefreshPending || !this.visible || !this.isExactItemMode || !session ||
            session.accountId !== this.loginService.getCurrentAccountId() ||
            session.userId !== this.loginService.getCurrentUserId() ||
            session.token !== this.loginService.getToken() ||
            this.itemFormModalVisible || this.deleteConfirmationModalVisible ||
            this.isItemMutationInProgress) return;
        this.exactRefreshPending = false;
        this.loadExactEvidenceDetail();
    }

    private markExactDraftStale(): void {
        this.exactDraftStale = true;
        this.itemUserDraftChanged();
        const message = "This evidence requirement changed. Close and reopen this action to use its latest details.";
        if (this.itemFormModalVisible) this.itemFormErrorMessage = message;
        if (this.deleteConfirmationModalVisible) this.deleteConfirmationErrorMessage = message;
    }

    private requestListRefresh(): void {
        if (this.itemFormModalVisible || this.deleteConfirmationModalVisible ||
            this.bulkEditModalVisible || this.isItemMutationInProgress) {
            this.listRefreshPending = true;
            return;
        }
        if (this.listRefreshTimer !== null) return;
        this.listRefreshTimer = setTimeout(() => {
            this.listRefreshTimer = null;
            if (!this.visible || this.isExactItemMode) return;
            if (this.itemFormModalVisible || this.deleteConfirmationModalVisible ||
                this.bulkEditModalVisible || this.isItemMutationInProgress) {
                this.listRefreshPending = true;
                return;
            }
            this.loadServerData(false);
        }, 0);
    }

    private flushListRefresh(): void {
        if (!this.listRefreshPending || this.itemFormModalVisible ||
            this.deleteConfirmationModalVisible || this.bulkEditModalVisible ||
            this.isItemMutationInProgress) return;
        this.listRefreshPending = false;
        this.requestListRefresh();
    }

    private clearListRefresh(): void {
        if (this.listRefreshTimer !== null) clearTimeout(this.listRefreshTimer);
        this.listRefreshTimer = null;
        this.listRefreshPending = false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        const contextChanged = !!(changes["productLicenseId"] || changes["exactEvidenceId"] || changes["initialItemAction"] ||
            changes["expectedMerchantId"] ||
            (changes["visible"] && !this.visible));
        const emailSucceeded = !contextChanged && this.emailSubmissionPending &&
            changes["emailSuccessMessage"] && !!this.emailSuccessMessage;
        if (emailSucceeded) {
            this.notification.success("Evidence requirements sent", this.emailSuccessMessage);
        }

        const emailRequestCompleted = !contextChanged && this.emailSubmissionPending && (
            (changes["emailSending"] && !this.emailSending) ||
            (changes["emailSuccessMessage"] && !!this.emailSuccessMessage) ||
            (changes["emailFailureMessage"] && !!this.emailFailureMessage)
        );
        if (emailRequestCompleted) {
            this.emailSubmissionPending = false;
            if (this.emailSuccessMessage) this.returnToEvidenceList();
            else if (this.emailFailureMessage && this.isEmailPanelVisible &&
                !this.loading && !this.recipientSummary) {
                if (this.emailEvidenceRequirementIds.length) this.loadRecipientSummary();
                else this.returnToEvidenceList();
            }
        }

        if (contextChanged) {
            this.initialItemActionHandled = false;
            this.cancelItemUserFlow();
            this.clearListRefresh();
            this.serverRequestId += 1;
            this.recipientRequestId += 1;
            this.listRequestSession = null;
            this.exactRequestSession = null;
            this.exactEvidenceDetail = null;
            this.exactRefreshPending = false;
            this.exactDraftStale = false;
            this.persistedEvidenceItems = [];
            this.displayedEvidenceItems = [];
            this.canManagePersistedEvidenceItems = false;
            this.uploaderOptions = [];
            this.requirementOptions = [];
            this.secureStorageFileOptions = [];
            this.isEmailPanelVisible = false;
            this.emailSubmissionPending = false;
            this.recipientSummary = null;
            this.recipientLoading = false;
            this.recipientErrorMessage = "";
            this.emailErrorMessage = "";
            this.evidenceFiltersVisible = false;
            this.selectedEvidenceSort = "created_at_desc";
            this.clearEvidenceItemSelection();
            this.clearEvidenceItemFilters();
            this.cancelItemMutation();
            this.closeEvidenceItemModals();
        }

        if (changes["visible"] && this.visible) {
            this.selectedStatusFilters = this.initialStatusFilter
                ? this.normalizeStatusFilters([this.initialStatusFilter]) : [];
            this.evidenceFiltersVisible = this.selectedStatusFilters.length > 0;
            this.refreshDisplayedEvidenceItems();
        }

        if ((changes["productLicenseId"] || changes["exactEvidenceId"] ||
            changes["expectedMerchantId"] || changes["visible"]) && this.visible) {
            if (this.isExactItemMode) {
                this.loadExactEvidenceDetail();
            } else if (this.productLicenseId) {
                this.loadServerData();
            }
        }
    }

    readonly exactBodyStyle = { "max-height": "none", "overflow-y": "auto" };

    get isExactItemMode(): boolean {
        return this.exactEvidenceId !== null;
    }

    get currentExactItem(): AssessmentWorkItemEvidenceDetail | null {
        const item = this.exactEvidenceDetail;
        const session = this.exactRequestSession;
        return this.visible && this.isExactItemMode && item && session &&
            item.id === this.exactEvidenceId && item.productLicenseId === this.productLicenseId &&
            (this.expectedMerchantId === null || item.merchantId === this.expectedMerchantId) &&
            session.accountId === this.loginService.getCurrentAccountId() &&
            session.userId === this.loginService.getCurrentUserId() &&
            session.token === this.loginService.getToken() ? item : null;
    }

    get canExactUpload(): boolean { return this.currentExactItem?.capabilities.canUpload === true; }
    get limitedEvidenceEdit(): boolean { return this.isExactItemMode && this.currentExactItem?.capabilities.canEditEvidenceDetails === false; }
    get canExactEdit(): boolean { return this.currentExactItem?.capabilities.canEditEvidence === true; }
    get canExactDelete(): boolean { return this.currentExactItem?.capabilities.canDeleteEvidence === true; }
    get canExactEmail(): boolean { return this.currentExactItem?.capabilities.canEmailEvidence === true; }

    get title(): string {
        return this.isExactItemMode
            ? "Evidence Requirement"
            : `Scoped Evidence Required for this Assessment: ${this.persistedEvidenceItems.length}`;
    }

    get hasFilteredEvidenceItems(): boolean {
        return this.filteredPersistedEvidenceItems.length > 0;
    }

    get hasPersistedEvidenceItems(): boolean {
        return this.persistedEvidenceItems.length > 0;
    }

    get selectedEvidenceItemCount(): number {
        return this.selectedPersistedEvidenceItems.length;
    }

    // Future selected-item actions must consume this server-backed collection,
    // rather than the filtered cards currently visible in the modal.
    get selectedPersistedEvidenceItems(): PersistedEvidenceRequirement[] {
        return this.persistedEvidenceItems.filter((item) => this.selectedEvidenceItemIds.has(item.id));
    }

    // Export rows are derived only from the current server-backed selection. Linked-file
    // context is intentionally reduced to presence so exports do not expose storage IDs.
    get selectedPersistedEvidenceItemExportRows(): PersistedEvidenceItemExportRow[] {
        return this.selectedPersistedEvidenceItems.map((item) => ({
            evidenceName: item.evidenceName,
            statusLabel: this.getStatusLabel(item.status),
            requirementKeys: [...item.requirementKeys],
            assignedUserLabel: this.getAssignedUserLabel(item),
            hasLinkedSecureStorageFile: item.linkedSecureStorageFileId !== null
        }));
    }

    get hasSelectedEvidenceItems(): boolean {
        return this.selectedEvidenceItemCount > 0;
    }

    get isSpecificRequirementSelectionEnabled(): boolean {
        return !this.itemFormState.allRequirements;
    }

    get isAllowedEvidenceItemDueDate(): boolean {
        return !this.itemDueDateInvalid &&
            isAllowedDueDate(this.itemFormState.dueDate, this.itemFormInitialState?.dueDate ?? null);
    }

    get isEvidenceItemFormValid(): boolean {
        const hasEvidenceName = this.itemFormState.evidenceName.trim().length > 0;
        const hasRequirements = this.itemFormState.allRequirements || this.itemFormState.requirementKeys.length > 0;
        const hasValidStatus = this.evidenceRequirementStatuses.includes(this.itemFormState.status);

        return !this.itemUserChildOpen && !this.itemUserReturnPending && !this.itemUserRecheckFailed &&
            hasEvidenceName && hasRequirements && hasValidStatus && this.isAllowedEvidenceItemDueDate;
    }

    get hasBulkEditChanges(): boolean {
        return this.bulkEditFormState.evidenceName.trim().length > 0 ||
            this.bulkEditFormState.status !== null ||
            this.bulkEditFormState.dueDateMode !== "unchanged" ||
            this.bulkEditFormState.requirementSelectionMode !== "unchanged" ||
            this.bulkEditFormState.assignmentSelectionMode !== "unchanged";
    }

    get isBulkEditFormValid(): boolean {
        const hasSpecificRequirements = this.bulkEditFormState.requirementKeys.some((requirementKey) => (
            typeof requirementKey === "string" && requirementKey.trim().length > 0
        ));
        const hasSpecificAssignees = this.bulkEditFormState.assignedUserIds.some((userId) => (
            Number.isSafeInteger(userId) && userId > 0
        ));

        return !this.itemUserChildOpen && !this.itemUserReturnPending && !this.itemUserRecheckFailed && this.hasBulkEditChanges &&
            (this.bulkEditFormState.dueDateMode !== "set" || this.isAllowedBulkDueDate) &&
            (this.bulkEditFormState.requirementSelectionMode !== "specific" || hasSpecificRequirements) &&
            (this.bulkEditFormState.assignmentSelectionMode !== "specific" || hasSpecificAssignees);
    }

    get isBulkSpecificRequirementSelectionEnabled(): boolean {
        return this.bulkEditFormState.requirementSelectionMode === "specific";
    }

    get isBulkSpecificAssigneeSelectionEnabled(): boolean {
        return this.bulkEditFormState.assignmentSelectionMode === "specific";
    }

    get areAllPersistedEvidenceItemsSelected(): boolean {
        return this.hasPersistedEvidenceItems && this.selectedEvidenceItemCount === this.persistedEvidenceItems.length;
    }

    get activeFilterCount(): number {
        return this.selectedStatusFilters.length + this.selectedAssignedUserFilters.length;
    }

    get filteredPersistedEvidenceItems(): PersistedEvidenceRequirement[] {
        const normalizedQuery = this.evidenceSearchQuery.trim().toLocaleLowerCase();
        const selectedStatuses = new Set(this.selectedStatusFilters);
        const selectedUserIds = new Set(this.selectedAssignedUserFilters);
        const sort = this.evidenceSortOptions.find((option) => option.value === this.selectedEvidenceSort)!;

        return this.persistedEvidenceItems.filter((item) => {
            const matchesSearch = !normalizedQuery || (
                item.evidenceName.toLocaleLowerCase().includes(normalizedQuery) ||
                item.requirementKeys.some((requirementKey) => requirementKey.toLocaleLowerCase().includes(normalizedQuery))
            );
            const matchesStatus = !selectedStatuses.size || selectedStatuses.has(item.status);
            const matchesAssignedUser = !selectedUserIds.size || item.assignedUserIds.some((userId) => selectedUserIds.has(userId));

            return matchesSearch && matchesStatus && matchesAssignedUser;
        }).sort((left, right) => {
            const leftTime = left[sort.field] ? Date.parse(left[sort.field]!) : NaN;
            const rightTime = right[sort.field] ? Date.parse(right[sort.field]!) : NaN;
            // Missing or invalid timestamps stay last in either direction.
            if (!Number.isFinite(leftTime) && Number.isFinite(rightTime)) return 1;
            if (!Number.isFinite(rightTime) && Number.isFinite(leftTime)) return -1;
            return (leftTime - rightTime) * sort.direction || right.id - left.id;
        });
    }

    updateEvidenceSort(value: EvidenceItemSort): void {
        if (value === this.selectedEvidenceSort || !this.evidenceSortOptions.some((option) => option.value === value)) return;
        this.selectedEvidenceSort = value;
        this.refreshDisplayedEvidenceItems();
        this.changeDetectorRef.detectChanges();
    }

    updateEvidenceSearchQuery(value: string): void {
        this.evidenceSearchQuery = typeof value === "string" ? value : "";
        this.refreshDisplayedEvidenceItems();
        this.changeDetectorRef.detectChanges();
    }

    showEvidenceFilters(): void {
        this.evidenceFiltersVisible = true;
    }

    get canRequestEmail(): boolean {
        return !!this.recipientSummary &&
            (this.isExactItemMode ? this.canExactEmail : this.canManagePersistedEvidenceItems) &&
            this.recipientSummary.selectedItemCount === this.emailEvidenceRequirementIds.length &&
            this.isCurrentEmailSelection(this.recipientSummaryEvidenceIds) &&
            this.recipientSummary.recipients.length > 0 &&
            !this.recipientLoading &&
            !this.isEmailSubmissionInProgress;
    }

    get isEmailSubmissionInProgress(): boolean {
        return this.emailSubmissionPending || this.emailSending;
    }

    private loadServerData(resetSearch = true): void {
        this.clearListRefresh();
        if (!this.productLicenseId) {
            return;
        }

        const requestId = ++this.serverRequestId;
        // A changed list may have different recipients even when the selection count is unchanged.
        this.recipientRequestId += 1;
        this.recipientSummary = null;
        this.recipientSummaryEvidenceIds = [];
        this.recipientLoading = false;
        this.recipientErrorMessage = "";
        if (!this.isEmailSubmissionInProgress) {
            const hadEmailPanel = this.isEmailPanelVisible;
            this.isEmailPanelVisible = false;
            this.emailErrorMessage = "";
            if (hadEmailPanel) this.emailStatusCleared.emit();
        }
        if (resetSearch) {
            // A previous assessment's search term must not hide the newly loaded list.
            this.evidenceSearchQuery = "";
            this.refreshDisplayedEvidenceItems();
        }
        this.requirementOptions = [];
        this.loading = true;
        this.errorMessage = "";

        this.loadPersistedItemContext(requestId);
    }

    private loadExactEvidenceDetail(): void {
        const productLicenseId = this.productLicenseId;
        const evidenceId = this.exactEvidenceId;
        const expectedMerchantId = this.expectedMerchantId;
        const requestId = ++this.serverRequestId;
        this.exactRequestSession = null;
        this.exactEvidenceDetail = null;
        this.recipientRequestId += 1;
        this.recipientSummary = null;
        this.recipientSummaryEvidenceIds = [];
        this.recipientLoading = false;
        this.recipientErrorMessage = "";
        this.isEmailPanelVisible = false;
        this.emailSubmissionPending = false;
        this.emailErrorMessage = "";
        this.loading = true;
        this.errorMessage = "";
        if (!Number.isSafeInteger(productLicenseId) || !productLicenseId || productLicenseId <= 0 ||
            !Number.isSafeInteger(evidenceId) || !evidenceId || evidenceId <= 0) {
            this.showExactItemUnavailable();
            return;
        }

        const accountId = this.loginService.getCurrentAccountId();
        const userId = this.loginService.getCurrentUserId();
        const token = this.loginService.getToken();
        if (!accountId || !userId || !token) {
            this.showExactItemUnavailable();
            return;
        }
        const session = { accountId, userId, token };
        this.exactRequestSession = session;
        const isCurrent = () => this.visible && this.isExactItemMode &&
            this.productLicenseId === productLicenseId && this.exactEvidenceId === evidenceId &&
            this.expectedMerchantId === expectedMerchantId &&
            requestId === this.serverRequestId && this.exactRequestSession === session &&
            accountId === this.loginService.getCurrentAccountId() &&
            userId === this.loginService.getCurrentUserId() && token === this.loginService.getToken();

        this.exactAssessmentRoute = null;
        this.assessmentWorkItemsService.getEvidenceDetail(productLicenseId, evidenceId).subscribe({
            next: (detail) => {
                if (!isCurrent()) return;
                if (expectedMerchantId !== null && detail.merchantId !== expectedMerchantId) {
                    this.showExactItemUnavailable();
                    this.changeDetectorRef.detectChanges();
                    return;
                }
                this.exactEvidenceDetail = detail;
                this.productLicenses.loadTaskAssessmentChoices().subscribe({
                    next: (choices) => {
                        if (!isCurrent()) return;
                        const choice = choices.find((candidate) => candidate.id === productLicenseId);
                        this.exactAssessmentRoute = choice ? this.router.createUrlTree(
                            ['/' + choice.formKey], { queryParams: { productLicenseId: encodeBase64RouteValue(String(productLicenseId)) } }
                        ) : null;
                        this.changeDetectorRef.detectChanges();
                    },
                    error: () => { if (isCurrent()) this.exactAssessmentRoute = null; }
                });
                this.loading = false;
                if (!this.initialItemActionHandled && this.initialItemAction) {
                    this.initialItemActionHandled = true;
                    if (this.initialItemAction === "email" && detail.capabilities.canEmailEvidence) this.toggleEmailPanel();
                    if (this.initialItemAction === "edit" && detail.capabilities.canEditEvidence) this.openEditEvidenceItemForm(detail.id);
                    if (this.initialItemAction === "delete" && detail.capabilities.canDeleteEvidence) this.openEvidenceItemDeleteConfirmation(detail.id);
                    if (this.isDirectItemAction && !this.itemFormModalVisible && !this.deleteConfirmationModalVisible && !this.isEmailPanelVisible)
                        this.showExactItemUnavailable();
                }
                this.changeDetectorRef.detectChanges();
            },
            error: () => {
                if (!isCurrent()) return;
                this.showExactItemUnavailable();
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    private loadPersistedItemContext(requestId: number): void {
        const productLicenseId = this.productLicenseId;
        const accountId = this.loginService.getCurrentAccountId();
        const userId = this.loginService.getCurrentUserId();
        const token = this.loginService.getToken();
        if (!productLicenseId || !accountId || !userId || !token) {
            this.clearPersistedItemContext("This assessment is unavailable in your current scope.");
            return;
        }
        const session = { accountId, userId, token };
        this.listRequestSession = session;
        const isCurrent = () => this.productLicenseId === productLicenseId &&
            this.listRequestSession === session && this.isCurrentListRequest(requestId);

        this.evidenceRequirementsService.getPersistedEvidenceRequirements(productLicenseId).subscribe({
            next: ({ items, canManage }) => {
                if (!isCurrent()) return;

                this.persistedEvidenceItems = items;
                this.reconcileEvidenceItemSelection(items);
                this.refreshDisplayedEvidenceItems();
                this.canManagePersistedEvidenceItems = canManage;
                this.uploaderOptions = [];
                this.secureStorageFileOptions = [];
                this.refreshAssignedUserFilterOptions();
                if (canManage) {
                    this.loadUploaderOptions(requestId);
                    this.loadRequirementOptions(requestId);
                    this.loadSecureStorageFileOptions(requestId);
                }
                this.loading = false;
                if (this.isEmailPanelVisible && !this.isEmailSubmissionInProgress) {
                    if (this.emailEvidenceRequirementIds.length) this.loadRecipientSummary();
                    else this.returnToEvidenceList();
                }
                this.changeDetectorRef.detectChanges();
            },
            error: (error: unknown) => {
                if (!isCurrent()) return;
                this.clearPersistedItemContext(this.isEvidenceAccessLoss(error)
                    ? "This assessment is unavailable in your current scope."
                    : "Unable to load the evidence requirements for this assessment.");
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    private clearPersistedItemContext(message: string): void {
        this.serverRequestId += 1;
        this.listRequestSession = null;
        this.persistedEvidenceItems = [];
        this.refreshDisplayedEvidenceItems();
        this.clearEvidenceItemSelection();
        this.uploaderOptions = [];
        this.secureStorageFileOptions = [];
        this.requirementOptions = [];
        this.refreshAssignedUserFilterOptions();
        this.canManagePersistedEvidenceItems = false;
        this.recipientRequestId += 1;
        this.recipientSummary = null;
        this.recipientSummaryEvidenceIds = [];
        this.recipientLoading = false;
        this.recipientErrorMessage = "";
        this.isEmailPanelVisible = false;
        this.emailSubmissionPending = false;
        this.emailErrorMessage = "";
        this.cancelItemMutation();
        this.closeEvidenceItemModals();
        this.emailStatusCleared.emit();
        this.errorMessage = message;
        this.loading = false;
    }

    private isCurrentListRequest(requestId: number): boolean {
        const session = this.listRequestSession;
        return this.visible && !this.isExactItemMode && requestId === this.serverRequestId &&
            !!session && session.accountId === this.loginService.getCurrentAccountId() &&
            session.userId === this.loginService.getCurrentUserId() &&
            session.token === this.loginService.getToken();
    }

    private isCurrentEditorOptionsRequest(requestId: number): boolean {
        return this.isExactItemMode
            ? requestId === this.serverRequestId && this.itemFormModalVisible && !this.exactDraftStale &&
                this.currentExactItem?.capabilities.canEditEvidence === true
            : this.isCurrentListRequest(requestId);
    }

    private loadUploaderOptions(requestId: number): void {
        const optionVersion = ++this.uploaderOptionVersion;
        const editor = this.itemFormState;
        if (!this.productLicenseId) {
            return;
        }

        this.evidenceRequirementsService.getEvidenceUploadUsers(this.productLicenseId).subscribe({
            next: (users) => {
                if (optionVersion === this.uploaderOptionVersion && this.isCurrentEditorOptionsRequest(requestId)) {
                    if (this.itemFormModalVisible && this.itemFormState === editor &&
                        editor.assignedUserIds.some((id) => !users.some((user) => user.userId === id))) {
                        this.itemUserRecheckFailed = true;
                        this.itemFormErrorMessage = "A selected evidence assignee is no longer eligible. Your draft is preserved; reopen the editor to resolve the selection before saving.";
                        return;
                    }
                    this.uploaderOptions = users;
                    this.refreshAssignedUserFilterOptions();
                    this.changeDetectorRef.detectChanges();
                }
            },
            error: () => {
                if (optionVersion === this.uploaderOptionVersion && this.isCurrentEditorOptionsRequest(requestId)) {
                    this.uploaderOptions = [];
                    this.refreshAssignedUserFilterOptions();
                }
            }
        });
    }

    private loadRequirementOptions(requestId: number): void {
        if (!this.productLicenseId) {
            return;
        }

        this.evidenceRequirementsService.getEvidenceRequirementEditorOptions(this.productLicenseId).subscribe({
            next: (requirementKeys) => {
                if (!this.isCurrentEditorOptionsRequest(requestId)) {
                    return;
                }

                this.requirementOptions = requirementKeys;
                this.changeDetectorRef.detectChanges();
            },
            error: () => {
                if (this.isCurrentEditorOptionsRequest(requestId)) {
                    this.requirementOptions = [];
                }
            }
        });
    }

    private loadSecureStorageFileOptions(requestId: number): void {
        if (!this.productLicenseId) {
            return;
        }

        this.evidenceRequirementsService.getEvidenceSecureStorageFileOptions(this.productLicenseId,
            this.limitedEvidenceEdit ? this.exactEvidenceId! : undefined).subscribe({
            next: (files) => {
                if (this.isCurrentEditorOptionsRequest(requestId)) {
                    this.secureStorageFileOptions = files;
                    this.changeDetectorRef.detectChanges();
                }
            },
            error: () => {
                if (this.isCurrentEditorOptionsRequest(requestId)) {
                    this.secureStorageFileOptions = [];
                }
            }
        });
    }

    private loadRecipientSummary(): void {
        const selectedEvidenceRequirementIds = this.emailEvidenceRequirementIds;
        const productLicenseId = this.productLicenseId;
        const exactMode = this.isExactItemMode;
        const session = exactMode ? this.exactRequestSession : this.listRequestSession;
        if (!productLicenseId || !selectedEvidenceRequirementIds.length || !session) {
            return;
        }

        const requestId = ++this.recipientRequestId;
        const isCurrent = () => this.visible && requestId === this.recipientRequestId &&
            this.productLicenseId === productLicenseId && this.isExactItemMode === exactMode &&
            (exactMode ? this.exactRequestSession : this.listRequestSession) === session &&
            this.isCurrentEmailSelection(selectedEvidenceRequirementIds) &&
            session.accountId === this.loginService.getCurrentAccountId() &&
            session.userId === this.loginService.getCurrentUserId() &&
            session.token === this.loginService.getToken();
        this.recipientLoading = true;
        this.recipientErrorMessage = "";
        this.recipientSummary = null;
        this.recipientSummaryEvidenceIds = [];
        this.changeDetectorRef.detectChanges();

        this.evidenceRequirementsService.getEvidenceRecipientSummary(
            productLicenseId,
            selectedEvidenceRequirementIds
        ).subscribe({
            next: (summary) => {
                if (!isCurrent()) {
                    return;
                }

                this.recipientSummary = summary;
                this.recipientSummaryEvidenceIds = [...selectedEvidenceRequirementIds];
                this.recipientLoading = false;
                this.changeDetectorRef.detectChanges();
            },
            error: () => {
                if (!isCurrent()) {
                    return;
                }

                this.recipientErrorMessage = "Unable to load recipients for the selected evidence items.";
                this.recipientLoading = false;
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    private get emailEvidenceRequirementIds(): number[] {
        if (this.emailItemId !== null && !this.isExactItemMode) return [this.emailItemId];
        if (this.isExactItemMode) {
            const item = this.currentExactItem;
            return item?.capabilities.canEmailEvidence ? [item.id] : [];
        }
        return this.selectedPersistedEvidenceItems.map((item) => item.id);
    }

    private isCurrentEmailSelection(expectedIds: readonly number[]): boolean {
        const currentIds = this.emailEvidenceRequirementIds;
        return currentIds.length === expectedIds.length &&
            currentIds.every((id, index) => id === expectedIds[index]);
    }

    trackByEvidenceItemId(_index: number, item: PersistedEvidenceRequirement): number {
        return item.id;
    }

    getStatusLabel(status: PersistedEvidenceRequirement["status"]): string {
        return this.statusLabels[status];
    }

    getAssignedUserLabel(item: PersistedEvidenceRequirement): string {
        if (!item.assignedUserIds.length) {
            return "Unassigned";
        }

        return item.assignedUserIds
            .map((userId) => this.assignedUserDisplayNamesById.get(userId) || `User ${userId}`)
            .join(", ");
    }

    getRequirementSummary(item: Pick<PersistedEvidenceRequirement, "requirementKeys">): string {
        const requirementKeys = item.requirementKeys;
        if (!requirementKeys.length) {
            return "All in-scope controls";
        }

        const displayedRequirementKeys = requirementKeys.slice(0, 5).join(", ");
        const hiddenRequirementCount = requirementKeys.length - 5;
        return hiddenRequirementCount > 0
            ? `${displayedRequirementKeys} and ${hiddenRequirementCount} more`
            : displayedRequirementKeys;
    }

    getEvidenceUploadUserLabel(user: EvidenceUploadUserOption): string {
        return user.email ? `${user.displayName} (${user.email})` : user.displayName;
    }

    isEvidenceItemSelected(evidenceRequirementId: number): boolean {
        return this.selectedEvidenceItemIds.has(evidenceRequirementId);
    }

    toggleEvidenceItemSelection(evidenceRequirementId: number, selected: boolean): void {
        if (
            !Number.isSafeInteger(evidenceRequirementId) || evidenceRequirementId <= 0 ||
            !this.persistedEvidenceItems.some((item) => item.id === evidenceRequirementId)
        ) {
            return;
        }

        if (this.selectedEvidenceItemIds.has(evidenceRequirementId) === selected) return;
        this.clearCopyFeedback();
        if (selected) {
            this.selectedEvidenceItemIds.add(evidenceRequirementId);
            this.onEmailSelectionChanged();
            return;
        }

        this.selectedEvidenceItemIds.delete(evidenceRequirementId);
        this.onEmailSelectionChanged();
    }

    selectAllEvidenceItems(): void {
        if (this.areAllPersistedEvidenceItemsSelected) return;
        this.clearCopyFeedback();
        this.selectedEvidenceItemIds = new Set(this.persistedEvidenceItems.map((item) => item.id));
        this.onEmailSelectionChanged();
    }

    deselectAllEvidenceItems(): void {
        if (!this.selectedEvidenceItemIds.size) return;
        this.clearEvidenceItemSelection();
        this.onEmailSelectionChanged();
    }

    private onEmailSelectionChanged(): void {
        this.recipientRequestId += 1;
        this.recipientSummary = null;
        this.recipientSummaryEvidenceIds = [];
        this.recipientLoading = false;
        this.recipientErrorMessage = "";
        if (this.isEmailPanelVisible && !this.isEmailSubmissionInProgress) {
            if (this.emailEvidenceRequirementIds.length) this.loadRecipientSummary();
            else this.returnToEvidenceList();
        }
    }

    copySelectedEvidenceItems(): void {
        const exportRows = this.selectedPersistedEvidenceItemExportRows;
        if (!exportRows.length) {
            return;
        }
        const requestId = ++this.copyRequestId;
        if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
            this.copyErrorMessage = "Copy to clipboard is not available in this browser.";
            return;
        }

        const copyText = buildPersistedEvidenceItemCopyText(exportRows);
        this.copyErrorMessage = "";
        void navigator.clipboard.writeText(copyText).then(
            () => {
                if (requestId !== this.copyRequestId) {
                    return;
                }

                const message = `${exportRows.length} selected evidence item${exportRows.length === 1 ? "" : "s"} copied to clipboard.`;
                this.notification.success("Evidence requirements copied", message);
            },
            () => {
                if (requestId !== this.copyRequestId) {
                    return;
                }

                this.copyErrorMessage = "Unable to copy the selected evidence items to your clipboard.";
            }
        );
    }

    downloadSelectedEvidenceItems(): void {
        const exportRows = this.selectedPersistedEvidenceItemExportRows;
        if (!exportRows.length) {
            return;
        }
        if (typeof document === "undefined" || !document.body || typeof URL === "undefined") {
            return;
        }

        const csv = buildPersistedEvidenceItemCsv(exportRows);
        const objectUrl = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const link = document.createElement("a");
        try {
            link.href = objectUrl;
            link.download = "selected-evidence-requirements.csv";
            document.body.appendChild(link);
            link.click();
        } finally {
            link.remove();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        }
    }

    openBulkEditEvidenceItems(): void {
        if (!this.canManagePersistedEvidenceItems || !this.hasSelectedEvidenceItems) {
            return;
        }

        this.bulkEditItemIds = this.selectedPersistedEvidenceItems.map((item) => item.id);
        this.bulkEditFormState = this.createEmptyBulkEditFormState();
        this.bulkDueDatePickerValue = null;
        this.bulkDueDateTouched = false;
        this.bulkDueDateInvalid = false;
        this.bulkEditErrorMessage = "";
        this.cancelItemUserFlow();
        this.bulkEditModalVisible = true;
        this.refreshItemInvitationCapability();
    }

    closeBulkEditEvidenceItems(): void {
        if (this.isItemMutationInProgress || this.itemUserChildOpen || this.itemUserReturnPending) {
            return;
        }

        this.bulkEditModalVisible = false;
        this.cancelItemUserFlow();
        this.bulkEditItemIds = [];
        this.bulkEditFormState = this.createEmptyBulkEditFormState();
        this.bulkDueDatePickerValue = null;
        this.bulkDueDateTouched = false;
        this.bulkDueDateInvalid = false;
        this.bulkEditErrorMessage = "";
        this.flushListRefresh();
    }

    setBulkRequirementSelectionMode(mode: BulkRequirementSelectionMode): void {
        if (!this.isBulkRequirementSelectionMode(mode)) {
            return;
        }

        this.bulkEditFormState.requirementSelectionMode = mode;
        if (mode !== "specific") {
            this.bulkEditFormState.requirementKeys = [];
        }
    }

    setBulkAssignmentSelectionMode(mode: BulkAssignmentSelectionMode): void {
        if (!this.isBulkAssignmentSelectionMode(mode)) {
            return;
        }

        this.bulkEditFormState.assignmentSelectionMode = mode;
        if (mode !== "specific") {
            this.bulkEditFormState.assignedUserIds = [];
        }
    }

    saveBulkEvidenceItemEdits(): void {
        if (!this.canManagePersistedEvidenceItems || !this.productLicenseId || this.isItemMutationInProgress ||
            this.itemUserChildOpen || this.itemUserReturnPending || this.itemUserRecheckFailed) {
            return;
        }

        const payload = this.createBulkEvidenceItemPayload();
        if (!payload) {
            return;
        }

        const productLicenseId = this.productLicenseId;
        const requestId = ++this.itemMutationRequestId;
        this.isItemMutationInProgress = true;
        this.bulkEditErrorMessage = "";
        this.evidenceRequirementsService.bulkUpdatePersistedEvidenceRequirements(productLicenseId, payload).subscribe({
            next: (updatedItems) => {
                if (!this.isCurrentItemMutation(requestId, productLicenseId)) {
                    return;
                }

                this.isItemMutationInProgress = false;
                this.applyUpdatedEvidenceItems(updatedItems);
                this.closeBulkEditEvidenceItems();
                this.emitPersistedEvidenceItemsChanged();
                this.loadServerData();
            },
            error: (error: unknown) => {
                if (!this.isCurrentItemMutation(requestId, productLicenseId)) {
                    return;
                }

                this.isItemMutationInProgress = false;
                this.bulkEditErrorMessage = this.getEvidenceMutationErrorMessage(
                    error,
                    "Unable to update the selected evidence requirements."
                );
            }
        });
    }

    private itemUserDraftChanged(): void {
        this.itemUserRevision += 1;
        this.itemUserOriginChanges.next();
    }

    private cancelItemUserFlow(): void {
        this.itemUserScopeRefreshPending = false;
        this.itemUserDraftChanged();
        this.itemUserChildRequest?.unsubscribe();
        this.itemUserReturnRequest?.unsubscribe();
        this.itemUserCapabilityRequest?.unsubscribe();
        this.itemUserChildOpen = false;
        this.itemUserReturnPending = false;
        this.itemUserInviteAllowed = false;
        this.itemUserRecheckFailed = false;
        this.itemAssigneeDropdownOpen = false;
    }

    get canAddEvidenceItemUser(): boolean {
        return this.visible && this.itemFormModalVisible && this.itemUserInviteAllowed && !this.exactDraftStale &&
            !this.itemUserRecheckFailed && !this.itemUserChildOpen && !this.itemUserReturnPending && !this.isItemMutationInProgress &&
            (this.isExactItemMode ? this.canExactEdit && this.itemFormMode === "edit" &&
                this.itemFormState.evidenceRequirementId === this.exactEvidenceId : this.canManagePersistedEvidenceItems);
    }

    openAssessmentAssignments(event?: Event): void {
        event?.preventDefault(); event?.stopPropagation();
        const license = this.productLicenseId;
        const bulk = this.bulkEditModalVisible;
        const editor = bulk ? this.bulkEditFormState : this.itemFormState;
        const revision = this.itemUserRevision;
        const token = this.loginService.getToken();
        const identity = assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        const current = () => this.visible && this.productLicenseId === license && this.itemUserRevision === revision &&
            (bulk ? this.bulkEditModalVisible && this.bulkEditFormState === editor :
                this.itemFormModalVisible && this.itemFormState === editor) &&
            !this.isItemMutationInProgress && !this.itemUserChildOpen && !this.itemUserReturnPending &&
            !this.isEmailPanelVisible && token === this.loginService.getToken() &&
            identity === assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        if (!license || !token || !identity || !current()) return;
        // Reserve a tab while the click still has browser user activation.
        const tab = window.open("", "_blank");
        if (!tab) {
            this.notification.warning("New tab blocked", "Allow pop-ups for this site to open assessment assignees.");
            return;
        }
        tab.opener = null;
        this.productLicenses.loadTaskAssessmentChoices().subscribe({
            next: (choices) => {
                if (!current()) { tab.close(); return; }
                const choice = choices.find((item) => item.id === license);
                if (!choice) {
                    tab.close();
                    this.notification.error("Assessment unavailable", "Unable to open assignments for this assessment.");
                    return;
                }
                this.itemAssigneeDropdownOpen = false;
                this.bulkEvidenceAssignees?.setOpenState(false);
                tab.location.replace(this.router.serializeUrl(this.router.createUrlTree(
                    ["/merchants", "assign_users", encodeRouteId(choice.merchantId), encodeRouteId(choice.id)])));
            },
            error: () => {
                tab.close();
                if (current()) this.notification.error("Unable to load assessment", "Try opening assignments again.");
            }
        });
    }

    private refreshItemInvitationCapability(): void {
        this.itemUserCapabilityRequest?.unsubscribe();
        this.itemUserInviteAllowed = false;
        const bulk = this.bulkEditModalVisible;
        const license = this.productLicenseId, editor = bulk ? this.bulkEditFormState : this.itemFormState;
        const token = this.loginService.getToken(), identity = assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        if (!license || !token || !identity) return;
        this.itemUserCapabilityRequest = this.accountSettings.getUserAccess({ productLicenseId: license }).subscribe({
            next: (access) => {
                if (this.productLicenseId !== license || (bulk ? this.bulkEditFormState !== editor || !this.bulkEditModalVisible
                    : this.itemFormState !== editor || !this.itemFormModalVisible) ||
                    token !== this.loginService.getToken() || identity !== assessmentTaskSessionIdentity(this.loginService.getUserSnapshot())) return;
                this.itemUserInviteAllowed = access.assessment?.productLicenseId === license && access.capabilities.canInviteOnAssessment;
                this.changeDetectorRef.markForCheck();
            }, error: () => {
                if ((bulk ? this.bulkEditFormState : this.itemFormState) === editor) this.itemUserInviteAllowed = false;
            }
        });
    }

    openAddEvidenceItemUser(event: Event): void {
        event.preventDefault(); event.stopPropagation();
        const license = this.productLicenseId, editor = this.itemFormState, mode = this.itemFormMode;
        const element = this.evidenceItemOrigin?.nativeElement;
        if (!this.canAddEvidenceItemUser || !license || !element) return;
        const revision = this.itemUserRevision, exactId = this.exactEvidenceId;
        const token = this.loginService.getToken(), identity = assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        const currentContext = () => this.visible && this.itemFormModalVisible && this.productLicenseId === license &&
            this.exactEvidenceId === exactId && this.itemFormState === editor && this.itemFormMode === mode &&
            !this.exactDraftStale && token === this.loginService.getToken() &&
            identity === assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        const current = () => currentContext() && this.itemUserRevision === revision;
        const origin: UserDialogOrigin = {
            requiresEvidenceUpload: true,
            snapshot: () => ({ instance: this.itemFormState, isOpen: this.visible && this.itemFormModalVisible && !this.exactDraftStale,
                productLicenseId: this.productLicenseId ?? 0, revision: this.itemUserRevision, field: "evidence.assignedUserIds" }),
            changes: this.itemUserOriginChanges, element, launchEvent: event,
            closeDropdown: () => { this.itemAssigneeDropdownOpen = false; }, focus: () => this.evidenceItemAssignees?.focus()
        };
        this.itemUserChildOpen = true;
        this.itemUserChildRequest = this.addUserLauncher.openAssessment(license, undefined, origin).pipe(finalize(() => {
            if (this.itemFormState !== editor) return;
            this.itemUserChildOpen = false;
            if (currentContext() && this.itemUserScopeRefreshPending && !this.itemUserReturnPending) this.refreshItemUserEligibility();
            this.changeDetectorRef.markForCheck();
        })).subscribe({
            next: (result) => {
                if (!current() || result.outcome === "canceled" || result.outcome === "storage_user_converted") return;
                if (result.productLicenseId !== license) return;
                if (result.outcome === "delivery_failed" || result.outcome === "delivery_unknown") {
                    this.notification.warning("User saved", invitationResultMessage(result));
                }
                this.refreshItemUserEligibility(result.user.id);
            }, error: (error: unknown) => {
                if (!current()) return;
                this.itemFormErrorMessage = error instanceof UserAccessError ? error.message : "Unable to add a user. Your evidence draft is unchanged; reopen this editor to reload current access.";
            }
        });
    }

    get canAddBulkEvidenceUser(): boolean {
        return this.visible && !this.isExactItemMode && this.bulkEditModalVisible && this.canManagePersistedEvidenceItems &&
            this.itemUserInviteAllowed && this.isBulkSpecificAssigneeSelectionEnabled && !this.isItemMutationInProgress &&
            !this.itemUserChildOpen && !this.itemUserReturnPending && !this.itemUserRecheckFailed;
    }

    openAddBulkEvidenceUser(event: Event): void {
        event.preventDefault(); event.stopPropagation();
        const license = this.productLicenseId, editor = this.bulkEditFormState;
        const element = this.bulkEvidenceOrigin?.nativeElement;
        if (!this.canAddBulkEvidenceUser || !license || !element) return;
        const token = this.loginService.getToken(), identity = assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        const draft = JSON.stringify(editor), ids = this.bulkEditItemIds.join(",");
        const current = () => this.visible && this.bulkEditModalVisible && this.productLicenseId === license &&
            this.bulkEditFormState === editor && JSON.stringify(editor) === draft && this.bulkEditItemIds.join(",") === ids &&
            token === this.loginService.getToken() && identity === assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        const origin: UserDialogOrigin = {
            requiresEvidenceUpload: true,
            snapshot: () => ({ instance: this.bulkEditFormState, isOpen: current(), productLicenseId: this.productLicenseId ?? 0,
                revision: 0, field: "evidence.bulk.assignedUserIds" }),
            changes: this.itemUserOriginChanges, element, launchEvent: event,
            closeDropdown: () => this.bulkEvidenceAssignees?.setOpenState(false), focus: () => this.bulkEvidenceAssignees?.focus()
        };
        this.itemUserChildOpen = true;
        this.itemUserChildRequest = this.addUserLauncher.openAssessment(license, undefined, origin).pipe(finalize(() => {
            if (this.bulkEditFormState !== editor) return;
            this.itemUserChildOpen = false;
            if (current() && this.itemUserScopeRefreshPending && !this.itemUserReturnPending) this.refreshBulkUserEligibility();
            this.changeDetectorRef.markForCheck();
        })).subscribe({
            next: (result) => {
                if (!current() || result.outcome === "canceled" || result.outcome === "storage_user_converted" ||
                    result.productLicenseId !== license) return;
                if (result.outcome === "delivery_failed" || result.outcome === "delivery_unknown") {
                    this.notification.warning("User saved", invitationResultMessage(result));
                }
                this.refreshBulkUserEligibility(result.user.id);
            }, error: (error: unknown) => {
                if (current()) this.bulkEditErrorMessage = error instanceof UserAccessError ? error.message : "Unable to add a user. Your selected evidence draft is unchanged.";
            }
        });
    }

    private refreshBulkUserEligibility(returnedUserId?: number): void {
        const license = this.productLicenseId, editor = this.bulkEditFormState;
        if (!license || !this.bulkEditModalVisible || this.isExactItemMode) return;
        this.itemUserReturnRequest?.unsubscribe();
        this.uploaderOptionVersion += 1;
        this.itemUserScopeRefreshPending = false;
        const ids = [...this.bulkEditItemIds];
        const stamps = new Map(this.persistedEvidenceItems.filter((item) => ids.includes(item.id)).map((item) => [item.id, item.updatedAt]));
        const token = this.loginService.getToken(), identity = assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        const current = () => this.visible && this.bulkEditModalVisible && this.productLicenseId === license &&
            this.bulkEditFormState === editor && this.bulkEditItemIds.join(",") === ids.join(",") &&
            token === this.loginService.getToken() && identity === assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        this.itemUserReturnPending = true;
        this.listRefreshPending = true;
        this.itemUserReturnRequest = forkJoin({
            access: this.evidenceRequirementsService.getPersistedEvidenceRequirements(license),
            users: this.evidenceRequirementsService.getEvidenceUploadUsers(license)
        }).pipe(finalize(() => {
            if (!current()) return;
            this.itemUserReturnPending = false;
            if (this.itemUserScopeRefreshPending && !this.itemUserRecheckFailed) this.refreshBulkUserEligibility();
            this.changeDetectorRef.markForCheck();
        })).subscribe({
            next: ({ access, users }) => {
                if (!current()) return;
                const eligible = new Set(users.map((user) => user.userId));
                const allowed = access.canManage && ids.length > 0 && ids.every((id) =>
                    stamps.has(id) && access.items.some((item) => item.id === id && item.updatedAt === stamps.get(id)));
                if (!allowed || editor.assignedUserIds.some((id) => !eligible.has(id)) ||
                    returnedUserId !== undefined && !eligible.has(returnedUserId)) {
                    this.itemUserRecheckFailed = true;
                    this.bulkEditErrorMessage = "Evidence access, selected items or eligible users changed. Your draft is preserved. Close and reopen Edit Selected before saving.";
                    return;
                }
                this.uploaderOptions = users;
                this.itemUserRecheckFailed = false;
                this.refreshAssignedUserFilterOptions();
                if (returnedUserId !== undefined) editor.assignedUserIds = [...new Set([...editor.assignedUserIds, returnedUserId])];
            }, error: () => {
                if (!current()) return;
                this.itemUserRecheckFailed = true;
                this.bulkEditErrorMessage = "The user may already be saved, but evidence access could not be confirmed. Your draft is preserved. Reopen Edit Selected; do not create the user again.";
            }
        });
    }

    private refreshItemUserEligibility(returnedUserId?: number): void {
        this.itemUserReturnRequest?.unsubscribe();
        this.uploaderOptionVersion += 1;
        this.itemUserScopeRefreshPending = false;
        const license = this.productLicenseId, editor = this.itemFormState, revision = this.itemUserRevision;
        const exactId = this.exactEvidenceId, mode = this.itemFormMode;
        const token = this.loginService.getToken(), identity = assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        if (!license || !this.itemFormModalVisible) return;
        const stamp = exactId !== null ? this.currentExactItem?.updatedAt
            : editor.evidenceRequirementId === null ? null : this.findPersistedEvidenceItem(editor.evidenceRequirementId)?.updatedAt;
        const sameContext = () => this.visible && this.itemFormModalVisible && this.productLicenseId === license &&
            this.exactEvidenceId === exactId && this.itemFormState === editor && this.itemFormMode === mode &&
            token === this.loginService.getToken() && identity === assessmentTaskSessionIdentity(this.loginService.getUserSnapshot());
        const permission = exactId !== null
            ? this.assessmentWorkItemsService.getEvidenceDetail(license, exactId).pipe(map((detail) =>
                detail.productLicenseId === license && detail.id === editor.evidenceRequirementId && detail.capabilities.canEditEvidence &&
                detail.updatedAt === stamp && (this.expectedMerchantId === null || detail.merchantId === this.expectedMerchantId)))
            : this.evidenceRequirementsService.getPersistedEvidenceRequirements(license).pipe(map(({ items, canManage }) =>
                canManage && (mode === "create" || items.some((item) => item.id === editor.evidenceRequirementId && item.updatedAt === stamp))));
        this.itemUserReturnPending = true;
        if (!this.isExactItemMode) this.listRefreshPending = true;
        this.itemUserReturnRequest = forkJoin({ allowed: permission, users: this.evidenceRequirementsService.getEvidenceUploadUsers(license) })
            .pipe(finalize(() => {
                if (sameContext()) {
                    this.itemUserReturnPending = false;
                    if (this.itemUserScopeRefreshPending && !this.itemUserRecheckFailed) this.refreshItemUserEligibility();
                    this.changeDetectorRef.markForCheck();
                }
            })).subscribe({
                next: ({ allowed, users }) => {
                    if (!sameContext() || this.exactDraftStale) return;
                    if (revision !== this.itemUserRevision) {
                        this.itemUserScopeRefreshPending = true;
                        return;
                    }
                    const eligible = new Set(users.map((user) => user.userId));
                    if (!allowed || editor.assignedUserIds.some((id) => !eligible.has(id)) ||
                        returnedUserId !== undefined && !eligible.has(returnedUserId)) {
                        this.itemUserRecheckFailed = true;
                        if (!allowed) {
                            if (this.isExactItemMode) { this.exactEvidenceDetail = null; this.exactRefreshPending = true; }
                            else this.canManagePersistedEvidenceItems = false;
                        }
                        this.itemFormErrorMessage = "This evidence item's access or eligible users changed. Your draft is preserved and cannot be saved. Close and reopen the editor to recheck current access.";
                        return;
                    }
                    this.uploaderOptions = users;
                    this.itemUserRecheckFailed = false;
                    this.refreshAssignedUserFilterOptions();
                    if (returnedUserId !== undefined) {
                        this.itemFormState.assignedUserIds = [...new Set([...editor.assignedUserIds, returnedUserId])];
                        this.itemUserDraftChanged();
                    }
                    if (!this.isExactItemMode) this.listRefreshPending = true;
                }, error: () => {
                    if (!sameContext()) return;
                    this.itemUserRecheckFailed = true;
                    this.itemUserInviteAllowed = false;
                    if (this.isExactItemMode) { this.exactEvidenceDetail = null; this.exactRefreshPending = true; }
                    else this.canManagePersistedEvidenceItems = false;
                    this.itemFormErrorMessage = "The user may already be saved, but evidence access could not be confirmed. Your draft is preserved. Close and reopen this editor; do not create the account again.";
                }
            });
    }

    openCreateEvidenceItemForm(): void {
        if (this.isExactItemMode || !this.canManagePersistedEvidenceItems) {
            return;
        }

        this.itemFormMode = "create";
        this.itemFormState = this.createEmptyItemFormState();
        this.itemFormInitialState = null;
        this.itemDueDatePickerValue = null;
        this.itemDueDateTouched = false;
        this.itemDueDateInvalid = false;
        this.itemFormErrorMessage = "";
        this.beginItemFormInitialization();
        this.itemFormModalVisible = true;
        this.refreshItemInvitationCapability();
        this.changeDetectorRef.detectChanges();
    }

    openEditEvidenceItemForm(evidenceRequirementId: number): void {
        if (this.isExactItemMode) {
            const item = this.currentExactItem;
            if (!item?.capabilities.canEditEvidence || item.id !== evidenceRequirementId) return;
            this.exactDraftStale = false;
            this.itemFormMode = "edit";
            this.itemFormState = {
                evidenceRequirementId: item.id,
                evidenceName: item.evidenceName,
                status: item.status,
                dueDate: item.dueDate,
                requirementKeys: item.requirements.map((requirement) => requirement.requirementKey),
                assignedUserIds: item.assignees.map((assignee) => assignee.userId),
                linkedSecureStorageFileId: item.linkedSecureStorageFileId,
                allRequirements: false
            };
            this.itemFormInitialState = this.cloneItemFormState(this.itemFormState);
            this.itemDueDatePickerValue = calendarDateToPickerDate(item.dueDate);
            this.itemDueDateTouched = false;
            this.itemDueDateInvalid = false;
            this.itemFormErrorMessage = "";
            this.beginItemFormInitialization();
            this.itemFormModalVisible = true;
            this.refreshItemInvitationCapability();
            const requestId = this.serverRequestId;
            if (!this.limitedEvidenceEdit) {
                this.loadUploaderOptions(requestId);
                this.loadRequirementOptions(requestId);
            }
            this.loadSecureStorageFileOptions(requestId);
            this.changeDetectorRef.detectChanges();
            return;
        }
        if (!this.canManagePersistedEvidenceItems) {
            const selected = this.findPersistedEvidenceItem(evidenceRequirementId);
            if (selected?.canEditAssigned && this.productLicenseId) this.evidenceLauncher.openItem(this.productLicenseId, selected.id, undefined, "edit");
            return;
        }

        const item = this.findPersistedEvidenceItem(evidenceRequirementId);
        if (!item) {
            return;
        }

        this.itemFormMode = "edit";
        this.itemFormState = {
            evidenceRequirementId: item.id,
            evidenceName: item.evidenceName,
            status: item.status,
            dueDate: item.dueDate,
            requirementKeys: [...item.requirementKeys],
            assignedUserIds: [...item.assignedUserIds],
            linkedSecureStorageFileId: item.linkedSecureStorageFileId,
            // The API expands allRequirements before persistence, so stored keys do not
            // preserve the user's original shortcut choice. Preserve the exact saved scope
            // instead of inferring an unsafe All requirements selection during editing.
            allRequirements: false,
        };
        this.itemFormInitialState = this.cloneItemFormState(this.itemFormState);
        this.itemDueDatePickerValue = calendarDateToPickerDate(item.dueDate);
        this.itemDueDateTouched = false;
        this.itemDueDateInvalid = false;
        this.itemFormErrorMessage = "";
        this.beginItemFormInitialization();
        this.itemFormModalVisible = true;
        this.refreshItemInvitationCapability();
        this.changeDetectorRef.detectChanges();
    }

    get canUploadEvidenceItemForm(): boolean {
        const productLicenseId = this.productLicenseId;
        const evidenceRequirementId = this.itemFormState.evidenceRequirementId;
        return this.visible && this.itemFormModalVisible && this.itemFormMode === "edit" &&
            (this.isExactItemMode
                ? this.canExactUpload && evidenceRequirementId === this.exactEvidenceId
                : this.canManagePersistedEvidenceItems && this.canOpenEvidenceUpload) &&
            !this.isItemMutationInProgress &&
            typeof productLicenseId === "number" && Number.isSafeInteger(productLicenseId) && productLicenseId > 0 &&
            typeof evidenceRequirementId === "number" && Number.isSafeInteger(evidenceRequirementId) && evidenceRequirementId > 0 &&
            (this.isExactItemMode || !!this.findPersistedEvidenceItem(evidenceRequirementId));
    }

    requestEvidenceItemUpload(): void {
        if (!this.canUploadEvidenceItemForm) return;
        const productLicenseId = this.productLicenseId!;
        const evidenceRequirementId = this.itemFormState.evidenceRequirementId!;

        if (this.isExactItemMode) this.exactRefreshPending = true;
        this.uploadRequested.emit({ productLicenseId, evidenceRequirementId });
    }

    requestEvidenceItemUploadForItem(evidenceRequirementId: number): void {
        const productLicenseId = this.productLicenseId;
        if (
            !this.visible || (this.isExactItemMode
                ? !this.canExactUpload || evidenceRequirementId !== this.exactEvidenceId
                : !this.canOpenEvidenceUpload) ||
            typeof productLicenseId !== "number" || !Number.isSafeInteger(productLicenseId) || productLicenseId <= 0 ||
            !Number.isSafeInteger(evidenceRequirementId) || evidenceRequirementId <= 0 ||
            (!this.isExactItemMode && !this.findPersistedEvidenceItem(evidenceRequirementId))
        ) {
            return;
        }

        if (this.isExactItemMode) this.exactRefreshPending = true;
        this.uploadRequested.emit({ productLicenseId, evidenceRequirementId });
    }

    closeEvidenceItemForm(): void {
        if (this.isItemMutationInProgress || this.itemUserChildOpen || this.itemUserReturnPending) {
            return;
        }

        const wasOpen = this.itemFormModalVisible;
        this.cancelItemUserFlow();

        this.itemFormModalVisible = false;
        this.itemFormMode = "create";
        this.itemFormState = this.createEmptyItemFormState();
        this.itemFormInitialState = null;
        this.itemDueDatePickerValue = null;
        this.itemDueDateTouched = false;
        this.itemDueDateInvalid = false;
        this.itemFormInitializing = false;
        this.itemFormInitializationId += 1;
        this.itemFormErrorMessage = "";
        this.exactDraftStale = false;
        if (wasOpen && this.finishDirectItemAction()) return;
        this.refreshExactItemWhenReady();
        this.flushListRefresh();
    }

    readonly bulkDueDateDisabled = (date: Date): boolean => isPickerDateDisabled(date, null);

    setBulkDueDateMode(mode: BulkDueDateMode): void {
        if (mode !== "unchanged" && mode !== "set" && mode !== "clear") return;
        if (mode === this.bulkEditFormState.dueDateMode) return;
        this.bulkEditFormState.dueDateMode = mode;
        this.bulkEditFormState.dueDate = null;
        this.bulkDueDatePickerValue = null;
        this.bulkDueDateTouched = false;
        this.bulkDueDateInvalid = false;
    }

    setBulkDueDate(date: Date | null): void {
        if (this.bulkEditFormState.dueDateMode !== "set") return;
        const dueDate = pickerDateToCalendarDate(date);
        this.bulkDueDateTouched = true;
        if (date !== null && dueDate === null) {
            this.bulkDueDateInvalid = true;
            return;
        }
        this.bulkDueDateInvalid = false;
        this.bulkEditFormState.dueDate = dueDate;
        this.bulkDueDatePickerValue = dueDate ? date : null;
    }

    get isAllowedBulkDueDate(): boolean {
        return !this.bulkDueDateInvalid && this.bulkEditFormState.dueDate !== null &&
            isAllowedDueDate(this.bulkEditFormState.dueDate, null);
    }

    readonly itemDueDateDisabled = (date: Date): boolean =>
        isPickerDateDisabled(date, this.itemFormInitialState?.dueDate ?? null);

    setEvidenceItemDueDate(date: Date | null): void {
        // A projected picker can emit an empty initial value while the edit dialog attaches.
        if (this.itemFormInitializing && date === null && this.itemFormState.dueDate !== null) return;
        const dueDate = pickerDateToCalendarDate(date);
        if (date !== null && dueDate === null) {
            this.itemDueDateInvalid = true;
            this.itemDueDateTouched = true;
            return;
        }
        this.itemDueDateInvalid = false;
        this.itemDueDatePickerValue = dueDate ? date : null;
        this.itemFormState.dueDate = dueDate;
        this.itemUserDraftChanged();
        this.itemDueDateTouched = true;
    }

    setAllRequirements(selected: boolean): void {
        if (typeof selected !== "boolean") {
            return;
        }

        // Keep the draft specific controls if the user toggles back. Saves with
        // All Controls still send only allRequirements, never the hidden keys.
        this.itemFormState.allRequirements = selected;
        this.itemUserDraftChanged();
    }

    setEvidenceItemName(value: unknown): void {
        // NzModal can emit an undefined model value while its projected form is
        // initializing. Ignore that lifecycle emission so it cannot erase the
        // selected item's saved evidence name before the user sees the editor.
        if (typeof value === "string" && (!this.itemFormInitializing || value.length > 0 || !this.itemFormState.evidenceName)) {
            this.itemFormState.evidenceName = value;
            this.itemUserDraftChanged();
        }
    }

    setEvidenceItemStatus(value: unknown): void {
        if (this.evidenceRequirementStatuses.includes(value as EvidenceRequirementStatus)) {
            this.itemFormState.status = value as EvidenceRequirementStatus;
            this.itemUserDraftChanged();
        }
    }

    setEvidenceItemRequirementKeys(value: unknown): void {
        if (this.itemFormState.allRequirements || !Array.isArray(value)) {
            return;
        }

        if (this.itemFormInitializing && !value.length && this.itemFormState.requirementKeys.length) {
            return;
        }

        this.itemFormState.requirementKeys = value.filter(
            (requirementKey): requirementKey is string => typeof requirementKey === "string"
        );
        this.itemUserDraftChanged();
    }

    setEvidenceItemAssignedUserIds(value: unknown): void {
        // An empty array is an intentional user clear; an undefined value is
        // only an initializing select control and must preserve saved assignees.
        if (Array.isArray(value)) {
            if (this.itemFormInitializing && !value.length && this.itemFormState.assignedUserIds.length) {
                return;
            }
            this.itemFormState.assignedUserIds = this.normalizeSelectedUserIds(value);
            this.itemUserDraftChanged();
        }
    }

    setEvidenceItemLinkedSecureStorageFileId(value: unknown): void {
        if (value === null || value === undefined) {
            if (!this.itemFormInitializing) {
                this.itemFormState.linkedSecureStorageFileId = null;
            this.itemUserDraftChanged();
            }
            return;
        }

        const fileId = Number(value);
        if (Number.isSafeInteger(fileId) && fileId > 0) {
            this.itemFormState.linkedSecureStorageFileId = fileId;
            this.itemUserDraftChanged();
        }
    }

    updateStatusFilters(value: unknown): void {
        const selectedValues = Array.isArray(value) ? value : value === null || value === undefined ? [] : [value];
        this.selectedStatusFilters = this.normalizeStatusFilters(
            selectedValues.filter((status): status is EvidenceRequirementStatus => typeof status === "string")
        );
        this.refreshDisplayedEvidenceItems();
        this.changeDetectorRef.detectChanges();
    }

    updateAssignedUserFilters(value: unknown): void {
        const selectedValues = Array.isArray(value) ? value : value === null || value === undefined ? [] : [value];
        this.selectedAssignedUserFilters = this.normalizeSelectedUserIds(selectedValues);
        this.refreshDisplayedEvidenceItems();
        this.changeDetectorRef.detectChanges();
    }

    saveEvidenceItemForm(): void {
        if (this.itemUserChildOpen || this.itemUserReturnPending || this.itemUserRecheckFailed) return;
        if ((!this.isExactItemMode && !this.canManagePersistedEvidenceItems) ||
            (this.isExactItemMode && (!this.canExactEdit || this.itemFormMode !== "edit" ||
                this.itemFormState.evidenceRequirementId !== this.exactEvidenceId || this.exactDraftStale)) ||
            !this.productLicenseId || this.isItemMutationInProgress) {
            return;
        }

        const submission = this.createEvidenceItemFormPayload();
        if (!submission) {
            return;
        }

        const productLicenseId = this.productLicenseId;
        const evidenceRequirementId = this.itemFormState.evidenceRequirementId;
        const isEdit = submission.mode === "edit";
        if (isEdit && !evidenceRequirementId) {
            this.itemFormErrorMessage = "The selected evidence requirement is no longer available.";
            return;
        }

        const requestId = ++this.itemMutationRequestId;
        this.isItemMutationInProgress = true;
        this.itemFormErrorMessage = "";
        const request = submission.mode === "edit"
            ? this.evidenceRequirementsService.updatePersistedEvidenceRequirement(productLicenseId, evidenceRequirementId!, submission.payload)
            : this.evidenceRequirementsService.createPersistedEvidenceRequirement(productLicenseId, submission.payload);

        request.subscribe({
            next: (savedItem) => {
                if (!this.isCurrentItemMutation(requestId, productLicenseId)) {
                    return;
                }

                this.isItemMutationInProgress = false;
                if (this.isExactItemMode) this.exactRefreshPending = false;
                this.closeEvidenceItemForm();
                if (this.isDirectItemAction) return;
                if (this.isExactItemMode) {
                    if (savedItem.id === this.exactEvidenceId) this.loadExactEvidenceDetail();
                    return;
                }
                this.applySavedEvidenceItem(savedItem, isEdit);
                this.emitPersistedEvidenceItemsChanged();
                this.loadServerData();
            },
            error: (error: unknown) => {
                if (!this.isCurrentItemMutation(requestId, productLicenseId)) {
                    return;
                }

                if (this.isExactItemMode && this.isEvidenceAccessLoss(error)) {
                    this.showExactItemUnavailable();
                    return;
                }
                this.isItemMutationInProgress = false;
                this.itemFormErrorMessage = this.getEvidenceMutationErrorMessage(
                    error,
                    `Unable to ${isEdit ? "update" : "create"} the evidence requirement.`
                );
                if (this.itemUserScopeRefreshPending) this.refreshItemUserEligibility();
            }
        });
    }

    openEvidenceItemDeleteConfirmation(evidenceRequirementId: number): void {
        if (this.isExactItemMode) {
            const item = this.currentExactItem;
            if (!item?.capabilities.canDeleteEvidence || item.id !== evidenceRequirementId) return;
            this.exactDraftStale = false;
            this.itemPendingDeletion = { id: item.id, evidenceName: item.evidenceName };
            this.deleteConfirmationErrorMessage = "";
            this.deleteConfirmationModalVisible = true;
            return;
        }
        if (!this.canManagePersistedEvidenceItems) {
            return;
        }

        const item = this.findPersistedEvidenceItem(evidenceRequirementId);
        if (!item) {
            return;
        }

        this.itemPendingDeletion = item;
        this.deleteConfirmationErrorMessage = "";
        this.deleteConfirmationModalVisible = true;
    }

    closeEvidenceItemDeleteConfirmation(): void {
        if (this.isItemMutationInProgress) {
            return;
        }

        const wasOpen = this.deleteConfirmationModalVisible;
        this.deleteConfirmationModalVisible = false;
        this.itemPendingDeletion = null;
        this.deleteConfirmationErrorMessage = "";
        this.exactDraftStale = false;
        if (wasOpen && this.finishDirectItemAction()) return;
        this.refreshExactItemWhenReady();
        this.flushListRefresh();
    }

    confirmEvidenceItemDeletion(): void {
        const evidenceRequirementId = this.itemPendingDeletion?.id;
        if (
            (this.isExactItemMode
                ? !this.canExactDelete || evidenceRequirementId !== this.exactEvidenceId || this.exactDraftStale
                : !this.canManagePersistedEvidenceItems) ||
            !this.productLicenseId || this.isItemMutationInProgress ||
            !Number.isSafeInteger(evidenceRequirementId) || evidenceRequirementId <= 0
        ) {
            return;
        }

        const productLicenseId = this.productLicenseId;
        const requestId = ++this.itemMutationRequestId;
        this.isItemMutationInProgress = true;
        this.deleteConfirmationErrorMessage = "";
        // The scoped API deletes only the evidence-item record; it never receives a file ID.
        this.evidenceRequirementsService.deletePersistedEvidenceRequirement(productLicenseId, evidenceRequirementId).subscribe({
            next: (deletedItem) => {
                if (!this.isCurrentItemMutation(requestId, productLicenseId)) {
                    return;
                }

                this.isItemMutationInProgress = false;
                if (this.isExactItemMode) this.exactRefreshPending = false;
                this.closeEvidenceItemDeleteConfirmation();
                if (this.isDirectItemAction) return;
                if (this.isExactItemMode) {
                    this.showExactItemUnavailable();
                    this.changeDetectorRef.detectChanges();
                    return;
                }
                this.persistedEvidenceItems = this.persistedEvidenceItems.filter((item) => item.id !== deletedItem.id);
                this.reconcileEvidenceItemSelection(this.persistedEvidenceItems);
                this.refreshDisplayedEvidenceItems();
                this.emitPersistedEvidenceItemsChanged();
                this.loadServerData();
            },
            error: (error: unknown) => {
                if (!this.isCurrentItemMutation(requestId, productLicenseId)) {
                    return;
                }

                if (this.isExactItemMode && this.isEvidenceAccessLoss(error)) {
                    this.showExactItemUnavailable();
                    return;
                }
                this.isItemMutationInProgress = false;
                this.deleteConfirmationErrorMessage = this.getEvidenceMutationErrorMessage(
                    error,
                    "Unable to delete the evidence requirement."
                );
            }
        });
    }

    clearFilters(): void {
        this.selectedStatusFilters = [];
        this.selectedAssignedUserFilters = [];
        this.evidenceSearchQuery = "";
        this.refreshDisplayedEvidenceItems();
        this.changeDetectorRef.detectChanges();
    }

    requestLinkedFile(
        item: PersistedEvidenceRequirement,
        action: EvidenceModalLinkedFileRequest["action"] = "manage"
    ): void {
        if (this.isExactItemMode) {
            return;
        }

        const evidenceRequirementId = Number(item?.id);
        const secureStorageFileId = Number(item?.linkedSecureStorageFileId);
        if (
            !this.productLicenseId ||
            !Number.isSafeInteger(evidenceRequirementId) || evidenceRequirementId <= 0 ||
            !Number.isSafeInteger(secureStorageFileId) || secureStorageFileId <= 0
        ) {
            return;
        }

        this.linkedFileRequested.emit({
            evidenceRequirementId,
            secureStorageFileId,
            action,
            openInNewTab: action === "preview" || action === "manage"
        });
    }

    requestExactLinkedFile(action: EvidenceModalLinkedFileRequest["action"]): void {
        const item = this.currentExactItem;
        if (!item?.linkedFileAccessible ||
            !item.linkedSecureStorageFileId) return;
        this.linkedFileRequested.emit({
            evidenceRequirementId: item.id,
            secureStorageFileId: item.linkedSecureStorageFileId,
            action,
            openInNewTab: action === "preview" || action === "manage"
        });
    }

    close(): void {
        if (this.itemUserChildOpen || this.itemUserReturnPending || this.isEmailPanelVisible) return;
        this.clearListRefresh();
        this.serverRequestId += 1;
        this.listRequestSession = null;
        this.exactRequestSession = null;
        this.exactEvidenceDetail = null;
        this.exactRefreshPending = false;
        this.recipientRequestId += 1;
        this.copyErrorMessage = "";
        this.isEmailPanelVisible = false;
        this.evidenceSearchQuery = "";
        this.evidenceFiltersVisible = false;
        this.clearEvidenceItemSelection();
        this.clearEvidenceItemFilters();
        this.cancelItemMutation();
        this.closeEvidenceItemModals();
        this.emailSubmissionPending = false;
        this.emailErrorMessage = "";
        this.recipientLoading = false;
        this.recipientErrorMessage = "";
        this.recipientSummary = null;
        this.recipientSummaryEvidenceIds = [];
        this.emailStatusCleared.emit();
        this.changeDetectorRef.detectChanges();
        this.visibleChange.emit(false);
    }

    openEvidenceItemEmail(item: PersistedEvidenceRequirement): void {
        if (!this.visible || this.isExactItemMode || !this.canManagePersistedEvidenceItems ||
            this.isItemMutationInProgress || !this.findPersistedEvidenceItem(item.id)) return;
        this.emailItemId = item.id;
        this.toggleEmailPanel();
    }

    toggleEmailPanel(): void {
        if (this.isExactItemMode ? !this.canExactEmail :
            !this.canManagePersistedEvidenceItems || (!this.emailItemId && !this.hasSelectedEvidenceItems)) {
            return;
        }

        this.isEmailPanelVisible = true;
        this.emailErrorMessage = "";
        this.changeDetectorRef.detectChanges();
        this.loadRecipientSummary();
    }

    closeEmailModal(): void {
        if (this.isEmailSubmissionInProgress) return;
        this.emailItemId = null;
        this.returnToEvidenceList();
    }

    returnToEvidenceList(): void {
        this.emailItemId = null;
        const wasOpen = this.isEmailPanelVisible;
        this.recipientRequestId += 1;
        this.recipientLoading = false;
        this.isEmailPanelVisible = false;
        this.emailErrorMessage = "";
        if (wasOpen) this.finishDirectItemAction();
    }

    requestEmail(): void {
        const evidenceRequirementIds = this.emailEvidenceRequirementIds;
        if (!this.canRequestEmail || !evidenceRequirementIds.length) {
            this.emailErrorMessage = "No assigned users with current upload access can receive the selected items.";
            return;
        }

        this.emailErrorMessage = "";
        if (!this.productLicenseId) {
            this.emailErrorMessage = "Select an active assessment before sending.";
            return;
        }

        this.emailSubmissionPending = true;
        this.emailRequested.emit({
            productLicenseId: this.productLicenseId,
            evidenceRequirementIds
        });
    }

    private reconcileEvidenceItemSelection(items: readonly PersistedEvidenceRequirement[]): void {
        const availableItemIds = new Set(items.map((item) => item.id));
        this.selectedEvidenceItemIds = new Set(
            Array.from(this.selectedEvidenceItemIds).filter((itemId) => availableItemIds.has(itemId))
        );
    }

    private createEmptyItemFormState(): EvidenceRequirementItemFormState {
        return {
            evidenceRequirementId: null,
            evidenceName: "",
            status: "not_submitted",
            dueDate: null,
            requirementKeys: [],
            assignedUserIds: [],
            linkedSecureStorageFileId: null,
            allRequirements: true
        };
    }

    private beginItemFormInitialization(): void {
        // Projected NZ Select controls may emit their empty initial value as the
        // modal is attached. Keep the persisted state authoritative until that
        // first render has completed; subsequent user events update it normally.
        this.itemFormInitializing = true;
        const initializationId = ++this.itemFormInitializationId;
        setTimeout(() => {
            if (initializationId === this.itemFormInitializationId) {
                this.itemFormInitializing = false;
            }
        });
    }

    private cloneItemFormState(state: EvidenceRequirementItemFormState): EvidenceRequirementItemFormState {
        return {
            ...state,
            requirementKeys: [...state.requirementKeys],
            assignedUserIds: [...state.assignedUserIds]
        };
    }

    private createEmptyBulkEditFormState(): BulkEvidenceItemFormState {
        return {
            evidenceName: "",
            status: null,
            dueDateMode: "unchanged",
            dueDate: null,
            requirementSelectionMode: "unchanged",
            requirementKeys: [],
            assignmentSelectionMode: "unchanged",
            assignedUserIds: []
        };
    }

    private createBulkEvidenceItemPayload(): BulkUpdateEvidenceRequirementsPayload | null {
        const currentItemIds = new Set(this.persistedEvidenceItems.map((item) => item.id));
        const evidenceRequirementIds = [...new Set(this.bulkEditItemIds.filter((itemId) => (
            Number.isSafeInteger(itemId) && itemId > 0 && currentItemIds.has(itemId)
        )))];
        if (!evidenceRequirementIds.length || evidenceRequirementIds.length !== this.bulkEditItemIds.length) {
            this.bulkEditErrorMessage = "The selected evidence items have changed. Reopen Edit Selected and try again.";
            return null;
        }
        if (!this.hasBulkEditChanges) {
            this.bulkEditErrorMessage = "Select at least one value to update.";
            return null;
        }

        const payload: BulkUpdateEvidenceRequirementsPayload = { evidenceRequirementIds };
        const evidenceName = this.bulkEditFormState.evidenceName.trim();
        if (evidenceName) {
            payload.evidenceName = evidenceName;
        }
        if (this.bulkEditFormState.status) {
            payload.status = this.bulkEditFormState.status;
        }
        if (this.bulkEditFormState.dueDateMode === "set") {
            if (!this.isAllowedBulkDueDate) {
                this.bulkDueDateTouched = true;
                this.bulkEditErrorMessage = "Choose a valid future date.";
                return null;
            }
            payload.dueDate = this.bulkEditFormState.dueDate;
        } else if (this.bulkEditFormState.dueDateMode === "clear") {
            payload.dueDate = null;
        }

        if (this.bulkEditFormState.requirementSelectionMode === "all") {
            payload.allRequirements = true;
        } else if (this.bulkEditFormState.requirementSelectionMode === "specific") {
            const requirementKeys = [...new Set(this.bulkEditFormState.requirementKeys
                .filter((requirementKey): requirementKey is string => typeof requirementKey === "string")
                .map((requirementKey) => requirementKey.trim())
                .filter((requirementKey) => requirementKey.length > 0))];
            if (!requirementKeys.length) {
                this.bulkEditErrorMessage = "Select one or more requirements, or choose All requirements.";
                return null;
            }
            payload.requirementKeys = requirementKeys;
        }

        if (this.bulkEditFormState.assignmentSelectionMode === "unassigned") {
            payload.unassigned = true;
        } else if (this.bulkEditFormState.assignmentSelectionMode === "specific") {
            const assignedUserIds = [...new Set(this.bulkEditFormState.assignedUserIds
                .filter((userId) => Number.isSafeInteger(userId) && userId > 0))];
            if (!assignedUserIds.length) {
                this.bulkEditErrorMessage = "Select one or more users, or choose Unassigned / None.";
                return null;
            }
            payload.assignedUserIds = assignedUserIds;
        }

        return payload;
    }

    private isBulkRequirementSelectionMode(mode: unknown): mode is BulkRequirementSelectionMode {
        return mode === "unchanged" || mode === "all" || mode === "specific";
    }

    private isBulkAssignmentSelectionMode(mode: unknown): mode is BulkAssignmentSelectionMode {
        return mode === "unchanged" || mode === "unassigned" || mode === "specific";
    }

    private createEvidenceItemFormPayload():
        | { mode: "create"; payload: CreateEvidenceRequirementPayload }
        | { mode: "edit"; payload: UpdateEvidenceRequirementPayload }
        | null {
        if (this.limitedEvidenceEdit) {
            const initial = this.itemFormInitialState;
            if (!initial || !this.evidenceRequirementStatuses.includes(this.itemFormState.status)) return null;
            const payload: UpdateEvidenceRequirementPayload = {};
            if (initial.status !== this.itemFormState.status) payload.status = this.itemFormState.status;
            if (initial.linkedSecureStorageFileId !== this.itemFormState.linkedSecureStorageFileId)
                payload.linkedSecureStorageFileId = this.itemFormState.linkedSecureStorageFileId;
            return Object.keys(payload).length ? { mode: "edit", payload } : null;
        }
        const evidenceName = this.itemFormState.evidenceName.trim();
        const requirementKeys = [...new Set(this.itemFormState.requirementKeys
            .filter((requirementKey): requirementKey is string => typeof requirementKey === "string")
            .map((requirementKey) => requirementKey.trim())
            .filter((requirementKey) => requirementKey.length > 0))];
        // NZ Select can surface option values as numeric strings. Normalize at
        // the request boundary as well as in the change handlers so the
        // selected assignees are never silently omitted from a save.
        const assignedUserIds = this.normalizeSelectedUserIds(this.itemFormState.assignedUserIds);

        if (!evidenceName) {
            this.itemFormErrorMessage = "Enter an evidence name.";
            return null;
        }
        if (!this.evidenceRequirementStatuses.includes(this.itemFormState.status)) {
            this.itemFormErrorMessage = "Select a valid evidence status.";
            return null;
        }
        if (!this.itemFormState.allRequirements && !requirementKeys.length) {
            this.itemFormErrorMessage = "Select one or more specific controls, or choose All Controls.";
            return null;
        }
        if (!this.isAllowedEvidenceItemDueDate) {
            this.itemDueDateTouched = true;
            this.itemFormErrorMessage = "Choose a valid future date.";
            return null;
        }

        const payload: CreateEvidenceRequirementPayload = {
            evidenceName,
            status: this.itemFormState.status,
            linkedSecureStorageFileId: this.itemFormState.linkedSecureStorageFileId
        };
        if (this.itemFormState.dueDate !== null) payload.dueDate = this.itemFormState.dueDate;
        if (this.itemFormState.allRequirements) {
            payload.allRequirements = true;
        } else {
            payload.requirementKeys = requirementKeys;
        }
        payload.assignedUserIds = assignedUserIds;

        if (this.itemFormMode !== "edit") {
            return { mode: "create", payload };
        }

        const initialState = this.itemFormInitialState;
        if (!initialState) {
            this.itemFormErrorMessage = "The selected evidence requirement is no longer available.";
            return null;
        }

        const updatePayload: UpdateEvidenceRequirementPayload = {};
        if (evidenceName !== initialState.evidenceName) {
            updatePayload.evidenceName = evidenceName;
        }
        if (this.itemFormState.status !== initialState.status) {
            updatePayload.status = this.itemFormState.status;
        }
        if (this.itemFormState.dueDate !== initialState.dueDate) {
            updatePayload.dueDate = this.itemFormState.dueDate;
        }
        const requirementsChanged = this.itemFormState.allRequirements !== initialState.allRequirements ||
            !this.areStringListsEqual(requirementKeys, initialState.requirementKeys);
        if (requirementsChanged) {
            if (this.itemFormState.allRequirements) {
                updatePayload.allRequirements = true;
            } else {
                updatePayload.requirementKeys = requirementKeys;
            }
        }
        if (!this.areNumberListsEqual(assignedUserIds, initialState.assignedUserIds)) {
            updatePayload.assignedUserIds = assignedUserIds;
        }
        if (this.itemFormState.linkedSecureStorageFileId !== initialState.linkedSecureStorageFileId) {
            updatePayload.linkedSecureStorageFileId = this.itemFormState.linkedSecureStorageFileId;
        }
        if (!Object.keys(updatePayload).length) {
            this.itemFormErrorMessage = "Make a change before saving the evidence requirement.";
            return null;
        }

        return { mode: "edit", payload: updatePayload };
    }

    private areStringListsEqual(left: readonly string[], right: readonly string[]): boolean {
        return left.length === right.length && left.every((value, index) => value === right[index]);
    }

    private areNumberListsEqual(left: readonly number[], right: readonly number[]): boolean {
        return left.length === right.length && left.every((value, index) => value === right[index]);
    }

    private normalizeSelectedUserIds(value: unknown): number[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return [...new Set(value
            .map((userId) => Number(userId))
            .filter((userId) => Number.isSafeInteger(userId) && userId > 0))];
    }

    private cancelItemMutation(): void {
        this.itemMutationRequestId += 1;
        this.isItemMutationInProgress = false;
        this.itemFormErrorMessage = "";
        this.deleteConfirmationErrorMessage = "";
        this.bulkEditErrorMessage = "";
    }

    private isCurrentItemMutation(requestId: number, productLicenseId: number): boolean {
        return requestId === this.itemMutationRequestId && productLicenseId === this.productLicenseId;
    }

    private isEvidenceAccessLoss(error: unknown): boolean {
        if (!error || typeof error !== "object") return false;
        const status = (error as { status?: unknown }).status;
        return status === 401 || status === 403 || status === 404;
    }

    private showExactItemUnavailable(): void {
        this.serverRequestId += 1;
        this.exactRequestSession = null;
        this.exactEvidenceDetail = null;
        this.exactRefreshPending = false;
        this.uploaderOptions = [];
        this.requirementOptions = [];
        this.secureStorageFileOptions = [];
        this.assignedUserDisplayNamesById.clear();
        this.recipientRequestId += 1;
        this.recipientSummary = null;
        this.recipientLoading = false;
        this.recipientErrorMessage = "";
        this.isEmailPanelVisible = false;
        this.emailSubmissionPending = false;
        this.emailErrorMessage = "";
        this.clearEvidenceItemSelection();
        this.cancelItemMutation();
        this.closeEvidenceItemModals();
        this.loading = false;
        this.errorMessage = "This evidence requirement is unavailable.";
        this.emailStatusCleared.emit();
        if (this.isDirectItemAction) {
            this.notification.error("Evidence action unavailable", this.errorMessage);
            this.finishDirectItemAction();
        }
    }

    private getEvidenceMutationErrorMessage(error: unknown, fallbackMessage: string): string {
        if (!error || typeof error !== "object") {
            return fallbackMessage;
        }

        const response = error as { error?: { message?: unknown }; message?: unknown };
        const responseMessage = response.error?.message ?? response.message;
        return typeof responseMessage === "string" && responseMessage.trim().length > 0
            ? responseMessage.trim()
            : fallbackMessage;
    }

    private closeEvidenceItemModals(): void {
        this.emailItemId = null;
        this.closingItemModals = true;
        try {
            this.closeEvidenceItemForm();
            this.closeEvidenceItemDeleteConfirmation();
            this.closeBulkEditEvidenceItems();
        } finally { this.closingItemModals = false; }
    }

    private finishDirectItemAction(): boolean {
        if (!this.isDirectItemAction || this.closingItemModals) return false;
        this.visibleChange.emit(false);
        return true;
    }

    private applyUpdatedEvidenceItems(updatedItems: readonly PersistedEvidenceRequirement[]): void {
        const updatedItemsById = new Map(updatedItems.map((item) => [item.id, item]));
        this.persistedEvidenceItems = this.persistedEvidenceItems.map((item) => updatedItemsById.get(item.id) || item);
        this.reconcileEvidenceItemSelection(this.persistedEvidenceItems);
        this.refreshDisplayedEvidenceItems();
        this.refreshAssignedUserFilterOptions();
    }

    private applySavedEvidenceItem(savedItem: PersistedEvidenceRequirement, isEdit: boolean): void {
        this.persistedEvidenceItems = isEdit
            ? this.persistedEvidenceItems.map((item) => item.id === savedItem.id ? savedItem : item)
            : [...this.persistedEvidenceItems, savedItem];
        this.reconcileEvidenceItemSelection(this.persistedEvidenceItems);
        this.refreshDisplayedEvidenceItems();
        this.refreshAssignedUserFilterOptions();
    }

    private emitPersistedEvidenceItemsChanged(): void {
        this.persistedEvidenceItemsChanged.emit([...this.persistedEvidenceItems]);
    }

    private findPersistedEvidenceItem(evidenceRequirementId: number): PersistedEvidenceRequirement | null {
        if (!Number.isSafeInteger(evidenceRequirementId) || evidenceRequirementId <= 0) {
            return null;
        }

        return this.persistedEvidenceItems.find((item) => item.id === evidenceRequirementId) || null;
    }

    private clearEvidenceItemSelection(): void {
        this.selectedEvidenceItemIds.clear();
        this.clearCopyFeedback();
    }

    private clearCopyFeedback(): void {
        this.copyRequestId += 1;
        this.copyErrorMessage = "";
    }

    private clearEvidenceItemFilters(): void {
        this.selectedStatusFilters = [];
        this.selectedAssignedUserFilters = [];
        this.refreshDisplayedEvidenceItems();
    }

    private refreshDisplayedEvidenceItems(): void {
        this.displayedEvidenceItems = this.filteredPersistedEvidenceItems;
    }

    private refreshAssignedUserFilterOptions(): void {
        // The filter is a prospective assignment filter, so it must include all
        // users eligible to be assigned, not only users already used by an item.
        // Retain a historical assignment as a fallback so existing cards remain
        // filterable while uploader eligibility is loading or has changed.
        const assignedUserDisplayNamesById = new Map<number, string>(
            this.uploaderOptions.map((user) => [user.userId, user.displayName])
        );
        this.persistedEvidenceItems.forEach((item) => item.assignedUserIds.forEach((userId) => {
            if (!assignedUserDisplayNamesById.has(userId)) {
                assignedUserDisplayNamesById.set(userId, `User ${userId}`);
            }
        }));
        this.assignedUserDisplayNamesById = assignedUserDisplayNamesById;
        this.assignedUserFilterOptions = Array.from(this.assignedUserDisplayNamesById, ([userId, displayName]) => ({
            userId,
            displayName
        })).sort((left, right) => left.displayName.localeCompare(right.displayName));
    }

    private normalizeStatusFilters(statuses: readonly EvidenceRequirementStatus[]): EvidenceRequirementStatus[] {
        return [...new Set(statuses
            .map((status) => String(status).trim().toLowerCase() as EvidenceRequirementStatus)
            .filter((status) => this.evidenceRequirementStatuses.includes(status)))];
    }

    private normalizeUserFilters(userIds: readonly number[], availableUserIds: ReadonlySet<number>): number[] {
        return [...new Set(userIds.filter((userId) => (
            Number.isSafeInteger(userId) && userId > 0 && availableUserIds.has(userId)
        )))];
    }

}
